import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

function DownloadChart() {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalDownloads, setTotalDownloads] = useState(0);

  useEffect(() => {
    const fetchDownloads = async () => {
      try {
        setIsLoading(true);
        const startDate = '2024-09-23';
        const endDate = new Date().toISOString().split('T')[0];
        const response = await axios.get(`https://api.npmjs.org/downloads/range/${startDate}:${endDate}/fleem`);
        const formattedData = response.data.downloads.map(item => ({
          date: new Date(item.day).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
          downloads: item.downloads
        }));
        setData(formattedData);
        const total = formattedData.reduce((sum, item) => sum + item.downloads, 0);
        setTotalDownloads(total);
      } catch (error) {
        console.error('Error fetching download data:', error);
        setError('Failed to load download data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDownloads();
  }, []);

  const chartColors = {
    stroke: '#4FD1C5',
    fill: '#4FD1C5',
    gradient: ['#4FD1C5', '#2C7A7B']
  };

  return (
    <motion.section 
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full mt-28"
    >
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-center text-blue-400">Thriving Community</h2>
      <AnimatePresence>
        {isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex justify-center items-center h-48 sm:h-64 md:h-96"
          >
            <div className="animate-spin rounded-full h-16 w-16 sm:h-24 sm:w-24 border-t-2 border-b-2 border-blue-400"></div>
          </motion.div>
        ) : error ? (
          <motion.p
            key="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center text-red-500"
          >
            {error}
          </motion.p>
        ) : (
          <motion.div
            key="chart"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="h-48 sm:h-64 md:h-96 w-full relative"
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
                margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="colorDownloads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={chartColors.gradient[0]} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={chartColors.gradient[1]} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#555" />
                <XAxis
                  dataKey="date"
                  stroke="#888"
                  tick={{ fill: '#888', fontSize: 10 }}
                  tickLine={{ stroke: '#888' }}
                />
                <YAxis
                  stroke="#888"
                  tick={{ fill: '#888', fontSize: 10 }}
                  tickLine={{ stroke: '#888' }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#333', border: 'none', borderRadius: '4px' }}
                  labelStyle={{ color: '#fff' }}
                  itemStyle={{ color: chartColors.stroke }}
                  formatter={(value) => [`${value} downloads`]}
                  labelFormatter={(label) => label}
                />
                <Legend wrapperStyle={{ fontSize: '0.7rem', marginTop: '5px' }} />
                <Line
                  type="monotone"
                  dataKey="downloads"
                  stroke={chartColors.stroke}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6, fill: chartColors.fill }}
                  name="Downloads"
                  fill="url(#colorDownloads)"
                />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-2 sm:mt-4 text-center text-xs sm:text-sm text-gray-400"
      >
        Total downloads: {totalDownloads.toLocaleString()}
      </motion.p>
    </motion.section>
  );
}

export default DownloadChart;